/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../../shared/modals/ModalAwareScrollView';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import type { SupportTicketRow } from '../services/supportTicketsRepo';
import { hasUnreadSupportReply } from '../services/supportTicketsRepo';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../../shared/ticket/ensureTicketFonts'
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles'
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../../shared/ticket/TicketKpiStrip'
import { TicketListRow } from '../../../shared/ticket/TicketListRow'
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader'
import { TicketDeskFooter } from '../../../shared/ticket/TicketDeskFooter';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame'
import { ShellScreenLoading } from '../../../shared/layout/ShellScreenLoading'
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState'
import { useScreenLoadShell } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { useSupportUnreadCount } from '../../providers/SupportUnreadProvider';
import { SupportWorkspaceView } from '../components/SupportWorkspaceView';
import { SupportTicketCreateModal } from '../components/SupportTicketCreateModal';
import { SupportTicketDetailModal } from '../components/SupportTicketDetailModal';
import { matchesSearchAny } from '../../../shared/format/searchText';
import { FilterSheetButton } from '../../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../../shared/filters/FilterSheetModal'
import { OperationalFilterChipGroup, OperationalFilterFieldLabel, OperationalFilterSectionDivider, OperationalFilterSearchField } from '../../../shared/filters/OperationalFilterPrimitives';
import { SoftPressable } from '../../../shared/ui/SoftPressable';
import { useSupportTicketsList } from '../hooks/useSupportTicketsList';
import { supportTypeLabel, supportStatusLabel, supportStatusTone, SUPPORT_CREATE_A11Y_LABEL } from './supportPresentation';

export const MySupportReportsScreen = () => {
  const { layoutWebShell } = useContentLayout('dataWide');
  const { refresh: refreshSupportUnread } = useSupportUnreadCount();

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useFocusEffect(
    useCallback(() => {
      void refreshSupportUnread({ force: true });
    }, [refreshSupportUnread]),
  );

  const route = useRoute() as { params?: { ticketId?: string } };
  const userId = useAuthStore((s) => s.session?.user?.id);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | undefined>(undefined);
  const [rowsOverride, setRowsOverride] = useState<SupportTicketRow[] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailTicketId, setDetailTicketId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterUnread, setFilterUnread] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const searchInputRef = useRef<TextInput>(null);

  const list = useSupportTicketsList(userId);
  const allRows = rowsOverride ?? list.tickets;

  const filteredRows = useMemo(() => {
    let result = allRows;
    if (searchQuery.trim()) {
      result = result.filter((t) =>
        matchesSearchAny([t.body, supportTypeLabel(t.ticket_type)], searchQuery),
      );
    }
    if (filterType !== 'all') {
      result = result.filter((t) => t.ticket_type === filterType);
    }
    if (filterStatus !== 'all') {
      result = result.filter((t) => t.status === filterStatus);
    }
    if (filterUnread === 'unread') {
      result = result.filter((t) => hasUnreadSupportReply(t));
    }
    if (sortBy === 'oldest') {
      result = [...result].sort((a, b) => a.created_at.localeCompare(b.created_at));
    }
    return result;
  }, [allRows, searchQuery, filterType, filterStatus, filterUnread, sortBy]);

  const rows = filteredRows;

  const hasActiveFilters = filterType !== 'all' || filterStatus !== 'all' || filterUnread !== 'all' || sortBy !== 'newest';
  const activeFilterCount = [filterType !== 'all', filterStatus !== 'all', filterUnread !== 'all', sortBy !== 'newest'].filter(Boolean).length;

  const clearFilters = useCallback(() => {
    setFilterType('all');
    setFilterStatus('all');
    setFilterUnread('all');
    setSortBy('newest');
  }, []);

  useEffect(() => {
    setRowsOverride(null);
  }, [list.tickets]);

  useEffect(() => {
    if (!userId) {
      setError('Iniciá sesión para ver tus reportes.');
      return;
    }
    setError(null);
  }, [userId]);

  useEffect(() => {
    if (layoutWebShell && rows.length > 0) {
      const paramId = route.params?.ticketId;
      if (paramId && rows.some((row) => row.id === paramId)) {
        setSelectedTicketId(paramId);
      } else {
        setSelectedTicketId((prev) => prev ?? rows[0].id);
      }
    }
  }, [layoutWebShell, rows, route.params?.ticketId]);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'SupportReports',
    async () => {
      await list.reload();
      setError(null);
    },
    { minFocusIntervalMs: 60_000 }
  );

  useEffect(() => {
    const ticketId = route.params?.ticketId;
    if (ticketId) {
      setSelectedTicketId(ticketId);
      if (!layoutWebShell) {
        setDetailTicketId(ticketId);
      }
    }
  }, [route.params?.ticketId, layoutWebShell]);

  const onWriteSupport = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const onTicketUpdated = useCallback((updated: SupportTicketRow) => {
    setRowsOverride((prev) => (prev ?? list.tickets).map((row) => (row.id === updated.id ? updated : row)));
  }, [list.tickets]);

  const initialLoading = list.loading && allRows.length === 0 && !error;

  const createModal = (
    <SupportTicketCreateModal
      visible={createModalOpen}
      onClose={() => setCreateModalOpen(false)}
      onSubmitted={() => {
        setCreateModalOpen(false);
        void onRefresh();
      }}
    />
  );

  // —— Web: delegar a SupportWorkspaceView ——

  if (layoutWebShell) {
    return (
      <>
        <SupportWorkspaceView
          rows={allRows}
          selectedTicketId={selectedTicketId}
          onSelectTicket={setSelectedTicketId}
          onWriteSupport={onWriteSupport}
          refreshing={refreshing}
          onRefresh={() => void onRefresh()}
          error={error}
          loading={initialLoading}
          onTicketUpdated={onTicketUpdated}
          pageIndex={list.pageIndex}
          pageSize={list.pageSize}
          setPageSize={list.setPageSize}
          hasMore={list.hasMore}
          totalCount={list.totalCount}
          onLoadNextPage={list.loadNextPage}
          onLoadPreviousPage={list.loadPreviousPage}
        />
        {createModal}
      </>
    );
  }

  // —— Mobile: Square shell ——

  const typeOptions: Array<{ value: string; label: string }> = [
    { label: 'Todos', value: 'all' },
    { label: 'Soporte', value: 'support' },
    { label: 'Mejora', value: 'improvement' },
  ];
  const statusOptions: Array<{ value: string; label: string }> = [
    { label: 'Todos', value: 'all' },
    { label: 'Enviado', value: 'submitted' },
    { label: 'En curso', value: 'triaged' },
    { label: 'Cerrado', value: 'closed' },
  ];
  const unreadOptions: Array<{ value: string; label: string }> = [
    { label: 'Todos', value: 'all' },
    { label: 'Con respuesta nueva', value: 'unread' },
  ];
  const sortOptions: Array<{ value: string; label: string }> = [
    { label: 'Más reciente', value: 'newest' },
    { label: 'Más antiguo', value: 'oldest' },
  ];

  const filterSheet = (
    <FilterSheetModal
      visible={filterSheetOpen}
      onClose={() => setFilterSheetOpen(false)}
      onApply={() => setFilterSheetOpen(false)}
      onClear={clearFilters}
      hasActiveDraft={hasActiveFilters}
      appearance="ticket"
      title="Filtros"
    >
      <OperationalFilterFieldLabel>Tipo</OperationalFilterFieldLabel>
      <OperationalFilterChipGroup
        options={typeOptions}
        value={filterType}
        onChange={setFilterType}
      />
      <OperationalFilterSectionDivider />
      <OperationalFilterFieldLabel>Estado</OperationalFilterFieldLabel>
      <OperationalFilterChipGroup
        options={statusOptions}
        value={filterStatus}
        onChange={setFilterStatus}
      />
      <OperationalFilterSectionDivider />
      <OperationalFilterFieldLabel>Respuesta del equipo</OperationalFilterFieldLabel>
      <OperationalFilterChipGroup
        options={unreadOptions}
        value={filterUnread}
        onChange={setFilterUnread}
      />
      <OperationalFilterSectionDivider />
      <OperationalFilterFieldLabel>Orden</OperationalFilterFieldLabel>
      <OperationalFilterChipGroup
        options={sortOptions}
        value={sortBy}
        onChange={setSortBy}
      />
    </FilterSheetModal>
  );

  // —— KPIs ——

  const kpiCounts = useMemo(() => {
    const submitted = allRows.filter((t) => t.status === 'submitted').length;
    const triaged = allRows.filter((t) => t.status === 'triaged').length;
    const closed = allRows.filter((t) => t.status === 'closed').length;
    return { submitted, triaged, closed };
  }, [allRows]);

  const kpiItems = useMemo((): TicketKpiItem[] => [
    { label: 'Total', value: String(list.totalCount) },
    {
      label: 'Enviados',
      value: String(kpiCounts.submitted),
      tone: kpiCounts.submitted > 0 ? 'warn' : 'default',
    },
    {
      label: 'En curso',
      value: String(kpiCounts.triaged),
      tone: kpiCounts.triaged > 0 ? 'ok' : 'default',
    },
    { label: 'Cerrados', value: String(kpiCounts.closed) },
  ], [list.totalCount, kpiCounts.submitted, kpiCounts.triaged, kpiCounts.closed]);

  const writeCta = (
    <SoftPressable
      style={mobileStyles.writeCta}
      onPress={onWriteSupport}
      accessibilityRole="button"
      accessibilityLabel={SUPPORT_CREATE_A11Y_LABEL}
      testID="soporte.write"
    >
      <Ionicons name="headset-outline" size={18} color={ticketColors.brandInk} />
      <Text style={mobileStyles.writeCtaText}>Escribir a soporte</Text>
    </SoftPressable>
  );

  const filterTrigger = (
    <FilterSheetButton
      activeCount={activeFilterCount}
      onOpen={() => setFilterSheetOpen(true)}
      accessibilityLabel="Abrir filtros de reportes"
      variant="ticket"
    />
  );

  if (initialLoading) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ShellScreenLoading label="Cargando reportes…" />
      </ShellContentFrame>
    );
  }

  return (
    <>
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ModalAwareScrollView
          contentContainerStyle={mobileStyles.scroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <TicketModuleHeader
            kicker="Soporte"
            title="Tus reportes"
            meta="Seguimiento de tus consultas y las respuestas de Vuelto"
            trailing={writeCta}
            testID="soporte.header"
          />

          <View style={mobileStyles.kpiWrap}>
            <TicketKpiStrip items={kpiItems} variant="soft" layout="mobile" />
          </View>

          <View style={mobileStyles.searchRow}>
            <View style={mobileStyles.searchBox}>
              <Ionicons name="search-outline" size={18} color={ticketColors.ink3} />
              <TextInput
                ref={searchInputRef}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Buscar en tus reportes…"
                placeholderTextColor={ticketColors.ink3}
                style={mobileStyles.searchInput}
                accessibilityLabel="Buscar reportes"
                autoCorrect={false}
                returnKeyType="search"
                blurOnSubmit={false}
              />
              {searchQuery.trim().length > 0 ? (
                <TouchableOpacity
                  onPress={() => setSearchQuery('')}
                  accessibilityRole="button"
                  accessibilityLabel="Limpiar búsqueda"
                >
                  <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                </TouchableOpacity>
              ) : null}
            </View>
            {filterTrigger}
          </View>

          {error ? <Text style={mobileStyles.warn}>{error}</Text> : null}

          {rows.length === 0 && !error ? (
            <ModuleEmptyState
              variant="nest"
              kicker="Soporte"
              title={searchQuery || hasActiveFilters
                ? 'No se encontraron reportes'
                : 'Todavía no escribiste a soporte'}
              subtitle={searchQuery || hasActiveFilters
                ? 'Probá con otros términos de búsqueda o ajustá los filtros.'
                : 'Escribinos y el equipo de Vuelto te responde.'}
              primaryAction={!searchQuery && !hasActiveFilters
                ? { label: 'Escribir a soporte', onPress: onWriteSupport }
                : undefined}
            />
          ) : (
            <PaperSheet padded={false} style={mobileStyles.listSheet}>
              {rows.map((item, index) => {
                const unread = hasUnreadSupportReply(item);
                const statusLabel = unread ? 'Nuevo' : supportStatusLabel(item.status);
                const statusTone = unread ? 'warn' : supportStatusTone(item.status) === 'success' ? 'ok' : supportStatusTone(item.status) === 'danger' ? 'bad' : supportStatusTone(item.status) === 'warning' ? 'warn' : 'muted';
                return (
                  <TicketListRow
                    key={item.id}
                    title={supportTypeLabel(item.ticket_type)}
                    subtitle={item.body}
                    meta={item.replied_at ? 'Con respuesta' : null}
                    statusLabel={statusLabel}
                    statusTone={statusTone as 'ok' | 'bad' | 'warn' | 'muted'}
                    onPress={() => setDetailTicketId(item.id)}
                    showChevron
                    group={
                      rows.length === 1
                        ? 'solo'
                        : index === 0
                          ? 'grouped'
                          : index === rows.length - 1
                            ? 'groupedLast'
                            : 'grouped'
                    }
                    accessibilityLabel={`${supportTypeLabel(item.ticket_type)}, ${supportStatusLabel(item.status)}`}
                  />
                );
              })}
            </PaperSheet>
          )}

          {rows.length > 0 ? (
            <TicketDeskFooter
              pageIndex={list.pageIndex}
              pageSize={list.pageSize}
              productCount={allRows.length}
              totalCount={list.totalCount}
              hasMore={list.hasMore}
              loading={list.loading}
              onPreviousPage={list.loadPreviousPage}
              onNextPage={list.loadNextPage}
              embedded
            />
          ) : null}
        </ModalAwareScrollView>
      </ShellContentFrame>
      {filterSheet}
      {createModal}
      <SupportTicketDetailModal
        visible={detailTicketId !== null}
        ticketId={detailTicketId}
        initialTicket={allRows.find((r) => r.id === detailTicketId) ?? null}
        onClose={() => setDetailTicketId(null)}
        onTicketUpdated={onTicketUpdated}
      />
    </>
  );
};

const mobileStyles = StyleSheet.create({
  scroll: {
    gap: spacing.md,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
  },
  kpiWrap: {
    marginTop: spacing.xs,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    height: 44,
    backgroundColor: ticketColors.paper2,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: ticketColors.ink,
  },
  warn: {
    color: '#B45309',
    fontSize: 14,
    fontWeight: '500',
  },
  listSheet: {
    paddingVertical: spacing.xs,
  },
  writeCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    minHeight: 40,
  },
  writeCtaText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.brandInk,
  },
});
